import { toIsoDate } from '../format/date.js';
import type { CandidateHour, ContractLineInvoiceLine, ContractLineTask } from './invoicing.js';
import { lineTotal, round2, sumMoney } from './money.js';

/**
 * Serviceaftalen: timerne faktureres til aftalens timepris - the hours the developer picked in
 * OnePro (`todos.invoice_hours`), or the used hours when none is picked.
 *
 * En serviceaftale er ikke en projektaftale med en anden etiket. Den har ingen kontraktsum at
 * fakturere af, ingen milepaele og ingen buffer - den har **en timepris og en graense pr.
 * opgave**.
 *
 * Graensen - timer pr. opgave - er **ikke et fradrag**. Den er de timer, en opgave maa bruge, foer
 * kunden skal godkende resten; alt arbejde paa aftalen faktureres, ogsaa under graensen.
 * Godkendelsen er ikke bygget endnu, saa graensen paavirker hverken antal eller beloeb - den vises
 * kun paa kontraktsiden og i rapporten, se `hoursOverTaskLimit`.
 *
 * Funktionerne her er rene, af samme grund som invoicing.ts: previewet, oprettelsen og
 * kontraktsiden skal regne det samme og kan kun goere det, hvis der kun er én regel.
 */

/**
 * Aftalens vilkaar, som de staar i CRM.
 *
 * Begge kan mangle, og det er ikke det samme som nul. En serviceaftale uden timepris kan ikke
 * faktureres - se `serviceAgreementBlocker`. Uden timer pr. opgave kan den godt: graensen
 * paavirker ikke fakturaen.
 */
export interface ServiceAgreementTerms {
  /**
   * Timeprisen for alle timer paa aftalen - `Løbende serviceaftale` paa kundens konto i Dynamics
   * (`account.cr19f_lbendeserviceaftale`).
   */
  hourly_rate_excl_vat: number | null;
  /**
   * Timer pr. opgave - `cr19f_serviceaftaletimerpropgave`. Paa aftalen, hvis den er sat der,
   * ellers paa kundens konto. Graensen for, hvornaar kunden skal godkende en opgave - ikke et
   * fradrag paa fakturaen.
   */
  hours_per_task: number | null;
}

/**
 * Teksten paa serviceaftalens interne linje.
 *
 * Kunden ser den ikke paa fakturaen - dér staar aftalens nummer og navn, som paa enhver anden
 * aftale, se `customerInvoiceLines`. Den staar paa /fakturering/ny, hvor linjen skal kunne
 * skelnes fra en kontraktlinje paa en projektaftale.
 */
export const SERVICE_AGREEMENT_LINE_LABEL = 'Timer på serviceaftalen';

/**
 * Timerne paa én opgave ud over timer pr. opgave - dem, kunden skal godkende.
 *
 * **Ikke et fradrag.** Alle timer faktureres; tallet siger kun, hvor meget af en opgave der ligger
 * over graensen. Aldrig negativ.
 */
export function hoursOverTaskLimit(usedHours: number, hoursPerTask: number): number {
  return round2(Math.max(0, usedHours - Math.max(0, hoursPerTask)));
}

/**
 * Hvorfor serviceaftalen ikke kan faktureres, eller null naar den kan.
 *
 * **Timeprisen skal vaere der.** Uden den er der intet beloeb at saette paa timerne, og et gaet
 * paa en kundevendt faktura er ikke et svar. Timer pr. opgave kraeves ikke: graensen paavirker
 * ikke, hvad der faktureres.
 */
export function serviceAgreementBlocker(terms: ServiceAgreementTerms): string | null {
  if (terms.hourly_rate_excl_vat !== null && terms.hourly_rate_excl_vat > 0) return null;
  return (
    "Serviceaftalen mangler timepris ('Løbende serviceaftale' på kunden) i Dynamics, så timerne " +
    'kan ikke prissættes. Sæt feltet i CRM, og hent grundlaget igen.'
  );
}

/**
 * Serviceaftalens timer som én fakturalinje, med opgaverne under sig.
 *
 * **Én linje for hele aftalen**, fordi kunden ser aftalen - `ONC-1083 · Serviceaftale for
 * Lynddahl` - og ikke en opdeling i faser, som en serviceaftale ikke har. Opgaverne foelger med
 * i `tasks`, og det er dem, bilaget viser: opgavens navn og de timer, der er brugt paa den.
 *
 * **The hours the developer picked are invoiced**: `quantity` is each task's `invoice_hours`
 * from OnePro, or its `used_hours` when none is picked, and the line's quantity is the sum over the
 * chosen tasks. `used_hours` stays what was registered, so /fakturering/ny shows both.
 *
 * Ingen buffer, ingen retning i haanden og intet 'Gratis' - linjen er maerket
 * `service_agreement`, og de funktioner lader den vaere. Se `applyInvoiceBuffer` og
 * `applyTaskHours`.
 */
export function groupHoursForServiceAgreement(
  billable: readonly CandidateHour[],
  terms: { hourly_rate_excl_vat: number },
  excludedTaskIds: ReadonlySet<string> = new Set(),
): ContractLineInvoiceLine[] {
  const rate = terms.hourly_rate_excl_vat;
  const tasks = new Map<string, ContractLineTask>();

  for (const hour of billable) {
    const title = hour.task_title ?? 'Opgave uden titel';
    const key = hour.task_id ?? `title:${title}`;
    const existing = tasks.get(key);
    const billed = hour.invoice_hours ?? hour.duration_hours;

    if (existing) {
      existing.quantity = round2(existing.quantity + billed);
      existing.used_hours = round2((existing.used_hours ?? 0) + hour.duration_hours);
      existing.estimate_hours = existing.estimate_hours ?? hour.estimate_hours ?? null;
      existing.hour_ids.push(hour.id);
      continue;
    }

    tasks.set(key, {
      task_id: hour.task_id,
      title,
      quantity: round2(billed),
      used_hours: round2(hour.duration_hours),
      estimate_hours: hour.estimate_hours ?? null,
      adjusted: false,
      hour_ids: [hour.id],
      subtasks: [],
      excluded: hour.task_id !== null && excludedTaskIds.has(hour.task_id),
      /* En serviceaftale kender ikke aendringsanmodninger: alt arbejde faktureres. */
      is_change_request: false,
      covered_by_scope: false,
      free: false,
    });
  }

  if (tasks.size === 0) return [];

  const rows = [...tasks.values()].sort((a, b) => a.title.localeCompare(b.title, 'da'));

  const chosen = rows.filter((task) => !task.excluded);
  const quantity = round2(chosen.reduce((total, task) => total + task.quantity, 0));

  return [
    {
      // Aftalen kendes af kaldstedet, ikke af grupperingen. Se `withContract`.
      contract: null,
      contract_line: null,
      description: SERVICE_AGREEMENT_LINE_LABEL,
      quantity,
      unit: 'hours',
      unit_price_excl_vat: rate,
      total_excl_vat: lineTotal(quantity, rate),
      hour_ids: chosen.flatMap((task) => task.hour_ids),
      tasks: rows,
      is_change_request: false,
      service_agreement: true,
    },
  ];
}

/**
 * Hvad serviceaftalens timer er vaerd: alle timerne, gange timeprisen.
 *
 * Kortene paa /kontrakter/:id - WIP og 'Reel timepris' - regner paa det samme tal, fakturaen
 * ville komme frem til. Prisen er timeprisen fra kunden, ikke satsen paa timerne.
 *
 * Null naar timeprisen mangler: da kan vaerdien ikke kendes - se `serviceAgreementBlocker`.
 */
export function serviceAgreementValue(
  perTaskHours: readonly number[],
  terms: ServiceAgreementTerms,
): number | null {
  if (serviceAgreementBlocker(terms) !== null) return null;
  const rate = terms.hourly_rate_excl_vat as number;

  return sumMoney(perTaskHours.map((hours) => lineTotal(hours, rate)));
}

/** Noeglen paa en maaned uden faerdigdato - opgaver, der ikke er meldt faerdige endnu. */
export const UNCOMPLETED_MONTH = 'uncompleted';

/** Én maaned paa serviceaftalens Timer-fane. */
export interface ServiceHourMonth<T> {
  /** `YYYY-MM`, eller `UNCOMPLETED_MONTH` for opgaverne uden faerdigdato. */
  month: string;
  /** Brugte timer i maaneden. */
  hours: number;
  /** The hours to invoice in the month - each task's `invoice_hours`, picked in OnePro. */
  invoice_hours: number;
  /**
   * Heraf ud over timer pr. opgave - det, kunden skal godkende. Ikke et fradrag, se
   * `hoursOverTaskLimit`. Null naar graensen ikke kendes.
   */
  over_limit_hours: number | null;
  task_count: number;
  rows: T[];
}

/**
 * Opgaverne samlet pr. maaned, de blev meldt faerdige i - nyeste maaned foerst.
 *
 * Maaneden er `completed_at` og ikke `created_at`, af samme grund som fakturaperioden: arbejdet
 * hoerer til den maaned, det blev faerdigt i, ikke den, opgaven blev skrevet ned i. Se
 * `pendingHours` i API'et.
 *
 * **Opgaver uden faerdigdato samles til sidst** under `UNCOMPLETED_MONTH` frem for at falde ud.
 * Det er igangvaerende arbejde med timer paa, og en fane, der skjulte dem, ville vise et lavere
 * forbrug end det, der er brugt.
 *
 * Maaneden laeses af tidsstemplet som UTC - de foerste syv tegn - ligesom perioden paa
 * /fakturering/ny afgraenses i UTC. De to maa ikke placere den samme opgave i hver sin maaned.
 */
export function groupServiceHoursByMonth<
  T extends { completed_at: string | null; duration_hours: number; invoice_hours: number },
>(rows: readonly T[], hoursPerTask: number | null): Array<ServiceHourMonth<T>> {
  const months = new Map<string, T[]>();

  for (const row of rows) {
    const key = row.completed_at ? row.completed_at.slice(0, 7) : UNCOMPLETED_MONTH;
    months.set(key, [...(months.get(key) ?? []), row]);
  }

  return [...months.entries()]
    .map(([month, list]) => ({
      month,
      hours: round2(list.reduce((total, row) => total + row.duration_hours, 0)),
      invoice_hours: round2(list.reduce((total, row) => total + row.invoice_hours, 0)),
      over_limit_hours:
        hoursPerTask === null
          ? null
          : round2(
              list.reduce(
                (total, row) => total + hoursOverTaskLimit(row.duration_hours, hoursPerTask),
                0,
              ),
            ),
      task_count: list.length,
      rows: [...list].sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? '')),
    }))
    .sort((a, b) => {
      if (a.month === UNCOMPLETED_MONTH) return 1;
      if (b.month === UNCOMPLETED_MONTH) return -1;
      return b.month.localeCompare(a.month);
    });
}

/** Én maaned i oversigten over serviceaftalens timer - se `serviceHoursTimeline`. */
export interface ServiceHourTimelineMonth {
  /** `YYYY-MM`. */
  month: string;
  hours: number;
  over_limit_hours: number | null;
}

/**
 * Maanederne i oversigten, **uden huller** og aeldste foerst - fra den foerste maaned med
 * faerdigmeldte opgaver til og med den indevaerende.
 *
 * Hullerne fyldes med nul, fordi en maaned uden arbejde er en oplysning: en oversigt, der sprang
 * den over, ville lade to aktive maaneder staa side om side, som om de fulgte efter hinanden.
 * Aeldste foerst, som 'Fakturering pr. måned' paa dashboardet, saa de to laeses paa samme maade.
 *
 * Opgaver uden faerdigdato er ikke med: de har ingen maaned. Fanen viser dem for sig.
 */
export function serviceHoursTimeline(
  months: readonly { month: string; hours: number; over_limit_hours: number | null }[],
  today: Date,
): ServiceHourTimelineMonth[] {
  const known = new Map(
    months.filter((row) => row.month !== UNCOMPLETED_MONTH).map((row) => [row.month, row]),
  );
  if (known.size === 0) return [];

  const keys = [...known.keys()].sort();
  const first = keys[0] as string;
  const current = toIsoDate(today).slice(0, 7);
  const lastKnown = keys[keys.length - 1] as string;
  const last = lastKnown > current ? lastKnown : current;

  const timeline: ServiceHourTimelineMonth[] = [];
  let [year, month] = first.split('-').map(Number) as [number, number];

  for (;;) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const row = known.get(key);
    timeline.push({
      month: key,
      hours: row?.hours ?? 0,
      over_limit_hours: row ? row.over_limit_hours : 0,
    });
    if (key >= last) return timeline;

    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
}
